/**
 * SearchBarHooks - Custom hooks for SearchBar component
 * Extracted for better modularity and compliance with 250-line limit
 */

import { useState, useCallback, useRef, useEffect, KeyboardEvent } from 'react';
import type { SearchResult } from '../../types/search';

// Hook for managing search bar state
export const useSearchBarState = <T>(initialState: T) => {
  const [state, setState] = useState<T>(initialState);
  const mountedRef = useRef(true);

  const safeSetState = useCallback((updater: (prev: T) => T) => {
    if (mountedRef.current) {
      setState(updater);
    }
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { state, setState, safeSetState, mountedRef };
};

interface SearchBarState {
  query: string;
  isLoading: boolean;
  isOpen: boolean;
  results: unknown[];
  suggestions: SearchResult[];
  error: string | null;
  selectedIndex: number;
}

// Hook for keyboard navigation  
export const useSearchBarKeyboard = (
  _currentState: SearchBarState,
  setState: (updater: (prev: SearchBarState) => SearchBarState) => void,
  handleSuggestionSelect: (suggestion: SearchResult) => (prev: SearchBarState) => SearchBarState
) => {
  // Navigation action handlers - memoized to prevent dependency issues
  const navigationActions = useCallback(() => ({
    up: (prev: SearchBarState) => ({
      ...prev,
      selectedIndex: prev.selectedIndex > 0 ? prev.selectedIndex - 1 : prev.suggestions.length - 1,
    }),
    down: (prev: SearchBarState) => ({
      ...prev,
      selectedIndex: prev.selectedIndex < prev.suggestions.length - 1 ? prev.selectedIndex + 1 : 0,
    }),
    escape: (prev: SearchBarState) => ({ ...prev, isOpen: false, selectedIndex: -1 }),
    clear: (prev: SearchBarState) => ({ 
      ...prev, 
      query: '', 
      results: [], 
      suggestions: [], 
      isOpen: false, 
      selectedIndex: -1 
    }),
  }), []);

  // Handle enter key action - memoized for stable reference
  const handleEnterAction = useCallback((prev: SearchBarState) => {
    if (prev.selectedIndex >= 0 && prev.suggestions[prev.selectedIndex]) {
      const suggestion = prev.suggestions[prev.selectedIndex];
      const searchResult = {
        id: suggestion.id,
        title: (suggestion as { text?: string }).text || suggestion.id,
        category: suggestion.category || 'search',
        relevanceScore: 1
      };
      return handleSuggestionSelect(searchResult)(prev);
    }
    return prev;
  }, [handleSuggestionSelect]);

  const handleKeyboardNavigation = useCallback((action: string) => {
    setState((prev: SearchBarState) => {
      if (action === 'enter') {
        return handleEnterAction(prev);
      }
      
      const actions = navigationActions();
      const actionHandler = actions[action as keyof typeof actions];
      return actionHandler ? actionHandler(prev) : prev;
    });
  }, [setState, handleEnterAction, navigationActions]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        handleKeyboardNavigation('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        handleKeyboardNavigation('down');
        break;
      case 'Enter':
        event.preventDefault();
        handleKeyboardNavigation('enter');
        break;
      case 'Escape':
        handleKeyboardNavigation('escape');
        break;
    }
  }, [handleKeyboardNavigation]);

  return { handleKeyboardNavigation, handleKeyDown };
};

// Hook for focus management
export const useSearchBarFocus = (
  setState: (updater: (prev: SearchBarState) => SearchBarState) => void,
  mountedRef: React.MutableRefObject<boolean>
) => {
  const handleInputFocus = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true }));
  }, [setState]);

  const handleInputBlur = useCallback(() => {
    // Delay closing to allow for click events on suggestions
    setTimeout(() => {
      if (mountedRef.current) {
        setState(prev => ({ ...prev, isOpen: false }));
      }
    }, 150);
  }, [setState, mountedRef]);

  return { handleInputFocus, handleInputBlur };
};

// Hook for suggestion selection
export const useSuggestionSelection = (
  onSelect?: (result: SearchResult) => void,
  addSearch?: (query: string) => void,
  clearOnSelect?: boolean
) => {
  const handleSuggestionSelect = useCallback((suggestion: SearchResult) => {
    if (onSelect) onSelect(suggestion);
    if (addSearch) addSearch(suggestion.title);
    
    return (prev: SearchBarState) => ({
      ...prev,
      query: clearOnSelect ? '' : suggestion.title,
      isOpen: false,
      selectedIndex: -1,
    });
  }, [onSelect, addSearch, clearOnSelect]);

  return { handleSuggestionSelect };
};